/**
 * Currency descriptors. The database `currencies` table is the authority for which currencies a
 * platform offers; this module carries the ISO facts that must be right given only a code.
 */

export const CurrencyRoundingMode = {
  HALF_UP: 'HALF_UP',
  HALF_EVEN: 'HALF_EVEN',
  UP: 'UP',
  DOWN: 'DOWN',
} as const;
export type CurrencyRoundingMode =
  (typeof CurrencyRoundingMode)[keyof typeof CurrencyRoundingMode];

export interface CurrencyInfo {
  id?: string;
  code: string;
  name: string;
  symbol: string;
  /** ISO 4217 minor unit. NOT always 2 — see CURRENCY_DECIMALS. */
  decimals: number;
  roundingMode: CurrencyRoundingMode;
  symbolFirst: boolean;
  isActive?: boolean;
}

/** Which link of the branch › tenant chain answered. */
export type CurrencySource = 'BRANCH' | 'TENANT';

/**
 * `GET /branches/:id/currency` — the server's own resolution with the branch › tenant fallback
 * already applied, plus which link answered.
 *
 * The one call a client needs to denominate a screen: it carries no permission key (only branch
 * scope), so it answers for a waiter or a cashier who cannot list branches at all.
 */
export interface ResolvedCurrency {
  /** Registry row id, or null when only the denormalized code was available. */
  id: string | null;
  code: string;
  symbol: string;
  /** ISO 4217 minor unit — NEVER assume 2. */
  decimals: number;
  roundingMode: CurrencyRoundingMode;
  symbolFirst: boolean;
  source: CurrencySource;
}

/**
 * ISO 4217 minor units for every currency that is not 2.
 *
 * Assuming 2 is a correctness bug, not a rounding preference: a Kuwaiti venue billing 12.345 KWD
 * truncated to 12.35 loses a fil on every line, and a Japanese venue showing "¥1,200.00" is
 * simply wrong. Anything absent from this map is 2.
 */
export const CURRENCY_DECIMALS: Record<string, number> = {
  // zero-decimal
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
  PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  // three-decimal
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  // four-decimal
  CLF: 4, UYW: 4,
};

/** Minor units for a code, defaulting to 2 for anything unlisted. */
export function decimalsFor(code: string | null | undefined): number {
  if (!code) return 2;
  return CURRENCY_DECIMALS[code.toUpperCase()] ?? 2;
}

/** The set a new platform is seeded with. Region-appropriate, not exhaustive. */
export const DEFAULT_CURRENCIES: ReadonlyArray<Omit<CurrencyInfo, 'id'>> = [
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', decimals: 3, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', decimals: 3, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', decimals: 3, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'ج.م', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', decimals: 3, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'USD', name: 'US Dollar', symbol: '$', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'EUR', name: 'Euro', symbol: '€', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'GBP', name: 'Pound Sterling', symbol: '£', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: true },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', decimals: 2, roundingMode: 'HALF_UP', symbolFirst: false },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimals: 0, roundingMode: 'HALF_UP', symbolFirst: true },
];
